// Step 3's calls to Registration's actor phone API (api/Phone), the one the membership site uses
// (React-Membership: api/cwb-api/phone-verification.js, models/account.js, pages/phone). Used
// unchanged, with the token verify-email or the login returned (decided 2026-10-06, option A):
//   GET  /Phone/web/phone/{clientId}                 is a phone already confirmed?
//   POST /Phone/send/code                            text a code (reCAPTCHA v3 "phone_send")
//   POST /Phone/web/verify/phone/client/{clientId}   check it (reCAPTCHA "phone_verify")
// That API texts the code, limits sends and guesses (per account, number and IP), refuses a
// number confirmed on another account or from a country we don't serve, and writes the
// confirmation trial eligibility reads. Registration then records the step (POST
// …/submission/{id}/phone), checking that confirmation with SQL.
//
// Its errors come as flags, not codes: they become the flow's error codes here, and the page
// says them in its own words. Its English messages are never shown (no half-translated pages).

import i18next from "i18next";
import { getSubmissionAuth } from "../../../helpers/submissionAuth";
import { NETWORK_ERROR_MESSAGE_KEY } from "../../http";
import { IPhoneStatusDto, ISubmissionApiError, SubmissionApiErrorCode } from "./types";

// What the membership site uses: the server's code length (phoneCodeLength) and its resend wait.
export const PHONE_CODE_LENGTH = 4;
export const PHONE_RESEND_SECONDS = 30;

const phoneBase = () => `${(process.env.REACT_APP_REG_Api || "").replace(/\/+$/, "")}/Phone`;

const fail = (status: number, errorCode: SubmissionApiErrorCode, message = ""): ISubmissionApiError => ({
  status,
  errorCode,
  message,
});

const account = () => {
  const auth = getSubmissionAuth();
  if (!auth || !auth.jwt || !auth.clientId) throw fail(401, "unauthorized");
  return auth;
};

const phoneFetch = async (path: string, init: RequestInit = {}): Promise<Response> => {
  const auth = account();
  try {
    return await fetch(`${phoneBase()}${path}`, {
      ...init,
      mode: "cors",
      headers: { "Content-Type": "application/json", Authorization: auth.jwt, ...(init.headers || {}) },
    });
  } catch (e) {
    const err: any = new Error(i18next.t(NETWORK_ERROR_MESSAGE_KEY));
    err.isNetworkError = true;
    throw err;
  }
};

// The body, whatever shape it came in: flags ({ isPhoneAlreadyExists, … }), a validation model
// ({ message, errors }), or a bare string (the 429 throttle).
const bodyOf = async (resp: Response): Promise<any> => {
  try {
    return await resp.json();
  } catch (e) {
    return null;
  }
};

const errorFor = (status: number, body: any, step: "send" | "verify"): ISubmissionApiError => {
  const b = body && typeof body === "object" ? body : {};
  if (status === 401 || status === 403) return fail(status, "unauthorized");
  if (status === 429) return fail(429, step === "send" ? "sendLimit" : "tooManyAttempts");
  if (b.recaptchaFailed || b.missingRecaptchaToken) return fail(status, "recaptchaFailed");
  if (b.isPhoneAlreadyExists) return fail(status, "phoneAlreadyInUse");
  if (b.countryNotSupported) return fail(status, "phoneCountryNotSupported");
  if (b.isInvalidVerificationCode) return fail(status, "invalidCode");
  if (status === 400) {
    const fields = (Array.isArray(b.errors) ? b.errors : []).map((e: any) => String((e && e.field) || "").toLowerCase());
    if (step === "verify" && fields.indexOf("code") >= 0) return fail(400, "invalidCode");
    if (fields.indexOf("phone") >= 0) return fail(400, "invalidPhone");
    return fail(400, step === "send" ? "invalidPhone" : "invalidCode");
  }
  return fail(status, "serverError");
};

// What the membership site accepts before sending (helpers/validations.js): 8 to 15 digits,
// not starting with 0, country code included.
export const isPlausiblePhone = (e164: string): boolean => /^[1-9]\d{7,14}$/.test((e164 || "").replace(/^\+/, ""));

// "+16045551234" from whatever the input holds; the API normalizes to E.164 too, but only adds
// a country code for Canadian and US actors.
export const toE164 = (raw: string): string => {
  const digits = (raw || "").replace(/[^0-9]/g, "");
  return digits ? `+${digits}` : "";
};

// As the membership site shows it: the last 4 digits.
export const maskPhone = (e164: string): string => {
  const digits = (e164 || "").replace(/[^0-9]/g, "");
  return digits.length >= 4 ? `****${digits.slice(-4)}` : "****";
};

// The number on file comes back too, confirmed or not: it pre-fills the input.
export const getPhoneStatus = async (): Promise<IPhoneStatusDto> => {
  const { clientId } = account();
  const resp = await phoneFetch(`/web/phone/${clientId}`, { method: "GET" });
  if (resp.status === 204) return { isVerified: false };
  if (!resp.ok) throw errorFor(resp.status, await bodyOf(resp), "send");
  const body = (await bodyOf(resp)) || {};
  return { isVerified: !!body.isVerified, phone: body.description || undefined };
};

// The page sends a token every time; the API ignores it on a resend while the last code is
// valid, and checks it otherwise. Without one it answers missingRecaptchaToken.
export const sendPhoneCode = async (phone: string, recaptchaToken?: string): Promise<void> => {
  const { clientId } = account();
  const resp = await phoneFetch(`/send/code`, {
    method: "POST",
    body: JSON.stringify({ clientId, phone, recaptchaToken: recaptchaToken || undefined }),
  });
  if (!resp.ok) throw errorFor(resp.status, await bodyOf(resp), "send");
};

// The tracking data rides along, as on the membership site: a confirmed phone gates the trial.
// The page reads it (helpers' getTrackingData) and passes it down.
export const verifyPhoneCode = async (
  phone: string,
  code: string,
  recaptchaToken?: string,
  tracking: object = {}
): Promise<void> => {
  const { clientId } = account();
  const resp = await phoneFetch(`/web/verify/phone/client/${clientId}`, {
    method: "POST",
    body: JSON.stringify({ ...tracking, phone, code: Number(code), recaptchaToken: recaptchaToken || undefined }),
  });
  if (!resp.ok) throw errorFor(resp.status, await bodyOf(resp), "verify");
};
